import React, { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Container,
  Form,
  Modal,
  Spinner,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { useHeadquarters } from "../hooks/useHeadquarters";
import {
  useClassrooms,
  useCreateClassroom,
  useUpdateClassroom,
  useDeleteClassroom,
} from "../hooks/useClassrooms";

const EMPTY = { headquarters: "", name: "", capacity: "", is_active: true };

function sedeLabel(sede) {
  return sede.reference_name || sede.address || `Sede ${sede.id}`;
}

// ── Modal de alta/edición de aula ────────────────────────────────────────────
function AulaFormModal({ show, onHide, editing, sedes }) {
  const [v, setV] = useState(EMPTY);
  const createM = useCreateClassroom();
  const updateM = useUpdateClassroom();
  const mutation = editing ? updateM : createM;

  useEffect(() => {
    if (!show) return;
    if (editing) {
      setV({
        headquarters: editing.headquarters ?? "",
        name: editing.name ?? "",
        capacity: editing.capacity ?? "",
        is_active: editing.is_active ?? true,
      });
    } else {
      setV({ ...EMPTY, headquarters: sedes.length === 1 ? sedes[0].id : "" });
    }
  }, [editing, show, sedes]);

  const submit = (e) => {
    e.preventDefault();
    const payload = {
      headquarters: Number(v.headquarters),
      name: v.name.trim(),
      capacity: v.capacity === "" ? null : Number(v.capacity),
      is_active: v.is_active,
    };
    const body = editing ? { id: editing.id, ...payload } : payload;
    mutation.mutate(body, {
      onSuccess: () => {
        toast.success(editing ? "Aula actualizada" : "Aula creada");
        onHide();
      },
      onError: (err) =>
        toast.error(
          err?.response?.data?.headquarters ??
            err?.response?.data?.name?.[0] ??
            "No se pudo guardar el aula."
        ),
    });
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{editing ? "Editar aula" : "Nueva aula"}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={submit}>
        <Modal.Body>
          <Form.Group className="mb-3" controlId="aula-sede">
            <Form.Label>Sede *</Form.Label>
            <Form.Select
              value={v.headquarters}
              onChange={(e) => setV((s) => ({ ...s, headquarters: e.target.value }))}
              required
              style={{ minHeight: 44 }}
            >
              <option value="">Selecciona la sede…</option>
              {sedes.map((sede) => (
                <option key={sede.id} value={sede.id}>
                  {sedeLabel(sede)}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3" controlId="aula-name">
            <Form.Label>Nombre *</Form.Label>
            <Form.Control
              value={v.name}
              onChange={(e) => setV((s) => ({ ...s, name: e.target.value }))}
              placeholder="Ej. Aula 1, Salón Príncipe"
              required
              maxLength={60}
              style={{ minHeight: 44 }}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="aula-capacity">
            <Form.Label>Capacidad (opcional)</Form.Label>
            <Form.Control
              type="number"
              min="1"
              value={v.capacity}
              onChange={(e) => setV((s) => ({ ...s, capacity: e.target.value }))}
              placeholder="Cupo máximo de estudiantes"
              style={{ minHeight: 44 }}
            />
          </Form.Group>

          <Form.Check
            type="switch"
            id="aula-active"
            label="Aula activa"
            checked={v.is_active}
            onChange={(e) => setV((s) => ({ ...s, is_active: e.target.checked }))}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} style={{ minHeight: 44 }}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={mutation.isPending}
            style={{ minHeight: 44 }}
          >
            {mutation.isPending ? (
              <Spinner animation="border" size="sm" />
            ) : editing ? (
              "Guardar cambios"
            ) : (
              "Crear aula"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

// ── Página ───────────────────────────────────────────────────────────────────
function AulasPage() {
  const { data: sedes = [], isLoading: loadingSedes } = useHeadquarters();
  const { data: aulas = [], isLoading, isError } = useClassrooms();
  const deleteM = useDeleteClassroom();

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const openNew = () => {
    setEditing(null);
    setShowForm(true);
  };
  const openEdit = (aula) => {
    setEditing(aula);
    setShowForm(true);
  };
  const handleDelete = (aula) => {
    if (!window.confirm(`¿Eliminar el aula "${aula.name}"?`)) return;
    deleteM.mutate(aula.id, {
      onSuccess: () => toast.success("Aula eliminada"),
      onError: () => toast.error("No se pudo eliminar el aula."),
    });
  };

  const busy = loadingSedes || isLoading;

  return (
    <Container fluid className="py-3">
      <Toaster position="top-right" />

      <div className="d-flex justify-content-between align-items-center mb-1 flex-wrap gap-2">
        <h4 className="mb-0">Aulas</h4>
        <Button
          variant="primary"
          onClick={openNew}
          disabled={sedes.length === 0}
          style={{ minHeight: 44 }}
        >
          + Nueva aula
        </Button>
      </div>
      <p className="text-muted small mb-3">
        Cada aula pertenece a una sede. Podrás asignarlas a las clases y ver la
        ocupación por aula en el calendario.
      </p>

      {busy && (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" />
        </div>
      )}
      {isError && <Alert variant="danger">No se pudieron cargar las aulas.</Alert>}

      {!busy && sedes.length === 0 && (
        <Alert variant="info">
          Primero registra una <Link to="../sedes">sede</Link>; las aulas
          pertenecen a una sede.
        </Alert>
      )}

      {!busy &&
        sedes.map((sede) => {
          const rooms = aulas.filter((a) => a.headquarters === sede.id);
          return (
            <Card key={sede.id} className="border-0 shadow-sm mb-3">
              <Card.Header className="bg-white fw-medium d-flex align-items-center gap-2">
                <i className="mdi mdi-map-marker-outline text-muted" aria-hidden="true" />
                {sedeLabel(sede)}
                <span className="text-muted small ms-auto">
                  {rooms.length} aula{rooms.length === 1 ? "" : "s"}
                </span>
              </Card.Header>
              <Card.Body className="py-2">
                {rooms.length === 0 ? (
                  <p className="text-muted small mb-0 py-2">
                    Sin aulas en esta sede.
                  </p>
                ) : (
                  rooms.map((a) => (
                    <div
                      key={a.id}
                      className="d-flex justify-content-between align-items-center gap-2 py-2 border-bottom"
                    >
                      <div style={{ minWidth: 0 }}>
                        <span className="fw-medium">{a.name}</span>
                        {a.capacity != null && (
                          <Badge bg="secondary" className="ms-2">
                            cupo {a.capacity}
                          </Badge>
                        )}
                        {!a.is_active && (
                          <Badge bg="warning" text="dark" className="ms-2">
                            Inactiva
                          </Badge>
                        )}
                      </div>
                      <div className="d-flex gap-2 flex-shrink-0">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => openEdit(a)}
                        >
                          Editar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleDelete(a)}
                          disabled={deleteM.isPending}
                        >
                          Eliminar
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </Card.Body>
            </Card>
          );
        })}

      <AulaFormModal
        show={showForm}
        onHide={() => {
          setShowForm(false);
          setEditing(null);
        }}
        editing={editing}
        sedes={sedes}
      />
    </Container>
  );
}

export { AulasPage };
